// Pure helpers + types for variation form state. Mirrors the shape locked
// in sowFormState.ts so the two forms share idioms — modal state lives
// in the modal component; this module owns serialisation and field-error
// types only.
//
// `numberOverrideMode` flags whether the user has clicked the
// AutoIdentifierField's Edit affordance. Empty `number` ⇒ omit from the
// create payload (backend auto-generates VAR-{YYYY}-{NNN}); non-empty
// override ⇒ send the literal value (backend uniqueness-checks via the
// TRACKING_NUMBER sentinel and surfaces a 409 on collision).
//
// `valueImpact` is held as a string for input control fidelity (empty,
// partial typing, leading minus). The serialiser parses it to a signed
// integer at payload time. Backend stores ex-GST AUD whole dollars; the
// form only accepts integer input. `nonFinancial=true` short-circuits
// the value field, persisting `null` instead.
//
// Multi-target: `targets` carries the variation's full target list.
// `stateFromVariation` reads either the new `targets[]` or the legacy
// `variesType` + `variesId` fields. The write path always emits
// `targets`; allocations are stripped from the wire when the variation
// is non-financial, as a defence-in-depth against the form persisting
// an incoherent allocation set.

import type {
  CreateVariationInput,
  EvidenceLink,
  UpdateVariationInput,
  Variation,
  VariationStatus,
  VariationTarget,
} from "../../../../types";
import {
  clearAllocationsIfSingle,
  readTargets,
  stripAllocations,
} from "./variationTargets";

export interface VariationFormState {
  name: string;
  /** Empty string = let backend auto-generate. Set when the user has
   *  clicked Edit and typed a custom value. Re-cleared when the user
   *  reverts to the auto-generated default. */
  number: string;
  numberOverrideMode: boolean;
  status: VariationStatus;
  /** Multi-target list. Always at least one entry once the form is
   *  validated; empty during initial setup is rejected at submit. */
  targets: VariationTarget[];
  effectiveDate: string;
  /** String for input fidelity. Empty + nonFinancial=false = unset
   *  (validation rejects). When nonFinancial=true the value field is
   *  ignored and the payload carries `null`. */
  valueImpact: string;
  nonFinancial: boolean;
  description: string;
  evidenceLinks: EvidenceLink[];
  notes: string;
}

export type VariationFieldErrors = Partial<
  Record<keyof VariationFormState | "form", string>
>;

export function buildEmptyVariationFormState(): VariationFormState {
  return {
    name: "",
    number: "",
    numberOverrideMode: false,
    status: "proposed",
    targets: [{ kind: "contract" }],
    effectiveDate: "",
    valueImpact: "",
    nonFinancial: false,
    description: "",
    evidenceLinks: [],
    notes: "",
  };
}

export function stateFromVariation(item: Variation): VariationFormState {
  return {
    name: item.name,
    number: item.number,
    numberOverrideMode: false,
    status: item.status,
    targets: readTargets(item),
    effectiveDate: item.effectiveDate ?? "",
    valueImpact:
      item.valueImpact === null || item.valueImpact === undefined
        ? ""
        : String(item.valueImpact),
    nonFinancial: item.valueImpact === null,
    description: item.description ?? "",
    evidenceLinks: item.evidenceLinks ? [...item.evidenceLinks] : [],
    notes: item.notes ?? "",
  };
}

/** Parse the string-typed valueImpact into a signed AUD amount with up
 *  to 2 decimal places (cents precision). Accepts a leading + or −
 *  (Unicode minus from copy-paste also tolerated). NaN surfaces as null
 *  — the form's validate() rejects null when not flagged non-financial. */
export function parseValueImpact(raw: string): number | null {
  const cleaned = raw
    .replace(/[\s,$]/g, "")
    .replace(/^−/, "-"); // accept Unicode minus on input
  if (cleaned === "" || cleaned === "-" || cleaned === "+" || cleaned === ".")
    return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  // Round to 2dp so the wire payload matches the backend monetaryAmount
  // invariant; floating-point inputs like 19.999 collapse to 20.00.
  return Math.round(n * 100) / 100;
}

/**
 * Build the backend Create payload from form state. Trims free-text
 * fields, parses valueImpact (or substitutes null when nonFinancial),
 * and only emits `number` when the user has explicitly typed an
 * override.
 */
export function toCreateVariationInput(
  state: VariationFormState,
  contractId: string,
): CreateVariationInput {
  const valueImpact = state.nonFinancial
    ? null
    : (parseValueImpact(state.valueImpact) ?? 0);
  const targets = state.nonFinancial
    ? stripAllocations(state.targets)
    : clearAllocationsIfSingle(state.targets);
  const base: CreateVariationInput = {
    contractId,
    name: state.name.trim(),
    targets,
    effectiveDate: state.effectiveDate,
    valueImpact,
  };
  if (state.numberOverrideMode && state.number.trim().length > 0) {
    base.number = state.number.trim();
  }
  if (state.status && state.status !== "proposed") base.status = state.status;
  if (state.description.trim()) base.description = state.description.trim();
  if (state.evidenceLinks.length > 0) base.evidenceLinks = state.evidenceLinks;
  if (state.notes.trim()) base.notes = state.notes.trim();
  return base;
}

/**
 * Build the backend Update payload. Excludes `number` and `status` —
 * both are immutable on the standard PUT path. `targets` and
 * `valueImpact` ARE editable while the variation is `proposed`; the
 * backend's status-gated PUT enforces the post-decision lock. The
 * version is supplied by the caller (the modal / inline-expand) since
 * it lives on the source record, not the form state.
 */
export function toUpdateVariationInput(
  state: VariationFormState,
  contractId: string,
  version: number,
): UpdateVariationInput {
  const valueImpact = state.nonFinancial
    ? null
    : (parseValueImpact(state.valueImpact) ?? 0);
  const targets = state.nonFinancial
    ? stripAllocations(state.targets)
    : clearAllocationsIfSingle(state.targets);
  const input: UpdateVariationInput = {
    contractId,
    name: state.name.trim(),
    targets,
    effectiveDate: state.effectiveDate,
    valueImpact,
    description: state.description.trim() || undefined,
    evidenceLinks:
      state.evidenceLinks.length > 0 ? state.evidenceLinks : undefined,
    notes: state.notes.trim() || undefined,
    version,
  };
  return input;
}
